import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import InputField from "components/Input/InputField";
import UploadFile from "components/Input/UploadFile";
import WholeScreenLoader from "components/WholeScreenLoader";
import axios from "const/axios";
import axiosBase from "axios";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useState } from "react";
import { AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import { IoArrowBack } from "react-icons/io5";
import {
  MdSchool,
  MdLocationOn,
  MdEmail,
  MdPerson,
  MdBusiness,
  MdPublic,
  MdLock,
} from "react-icons/md";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import FeesBreakdownModal from "./FeesBreakdownModal";
import ScholarshipModal from "./ScholarshipModal";
import { formatDisplayDate } from "utils/dateFormat";

const AddUni = ({ color = "light" }) => {
  const [data, setData] = useState({
    uniId: null,
    name: null,
    abn: null,
    contactPerson: null,
    email: null,
    country: "Australia",
    state: null,
    zipCode: null,
    image: null,
    pricelistUrl: null,
    applicationFormUrl: null,
    gsDocumentsUrl: null,
    creditFormUrl: null,
    courses: [
      {
        courseId: null,
        uniCourseId: null,
        college: "",
        courseName: "",
        durationInWeeks: "",
        noOfSem: "",
        feePerSem: "",
        totalFees: "",
        maxScholarship: "",
        intakes: [],
        feesBreakdown: [
          {
            id: null,
            uuid: crypto.randomUUID(),
            maximum: "",
            average: "",
            minimum: "",
          },
        ],
        scholarships: [
          {
            id: null,
            uuid: crypto.randomUUID(),
            intake: "",
            scholarship: "",
            note: "",
          },
        ],
        enrollmentFee: "",
        materialFee: "",
        discount: "",
        programLevel: [],
        onshoreFees: [
          {
            feePerSem: "",
            totalFees: "",
            scholarship: "",
            feeAfterScholarship: "",
            discount: "",
            state: "New South Wales",
            uuid: crypto.randomUUID(),
            key1: "",
          },
        ],
        offshoreFees: {
          enrollmentFee: "",
          materialFee: "",
          feePerSem: "",
          totalFees: "",
          discount: "",
          feeAfterScholarship: "",
          scholarship: "",
          offShoreKey1: "",
        },
        englishLanguageRequirements: "",
        academicRequirements: "",
        otherRequirements: "",
        key1: "",
        key2: "",
        key3: "",
      },
    ],
    pricelistUrl: null,
    applicationFormUrl: null,
    gsDocumentsUrl: null,
    creditFormUrl: null,
  });
  const user = JSON.parse(localStorage.getItem("userDetail"));

  // Scope choice only applies at creation time — editing preserves whatever scope the
  // record already has (see UniversityService.resolveOrganizationIdForUpdate on the backend).
  const [orgOnly, setOrgOnly] = useState(false);
  const [feesModalOpen, setFeesModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [scholarshipModalOpen, setScholarshipModalOpen] = useState(false);
  const [selectedScholarshipCourse, setSelectedScholarshipCourse] = useState(null);
  const [selectedCourseIndex, setSelectedCourseIndex] = useState(null);

  // Agents
  const [agents, setAgents] = useState([]);
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [agentForm, setAgentForm] = useState({ name: "", email: "", phone: "" });

  const navigate = useNavigate();
  const [{ uniData }, { refetchUniData }] = useKothar();

  const fetchAgents = async (uniId) => {
    const id = Number(uniId);
    if (!Number.isFinite(id) || id <= 0) return;
    try {
      const res = await axiosBase.get(`${API_URL}/universities/${id}/agents`);
      setAgents(res?.data?.data || []);
    } catch {
      setAgents([]);
    }
  };

  const handleAddAgent = async () => {
    if (!agentForm.name.trim()) {
      toast.warning("Agent name required");
      return;
    }
    try {
      await axiosBase.post(`${API_URL}/universities/${data.id}/agents`, agentForm);
      toast.success("Agent added");
      setAgentDialogOpen(false);
      setAgentForm({ name: "", email: "", phone: "" });
      fetchAgents(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to add agent");
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm("Delete this agent?")) return;
    try {
      await axiosBase.delete(`${API_URL}/universities/${data.id}/agents/${agentId}`);
      toast.success("Agent deleted");
      fetchAgents(data.id);
    } catch {
      toast.error("Failed to delete agent");
    }
  };

  const { state } = useLocation();
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData((prevState) => ({ ...prevState, [name]: value }));
  };

  const { id } = useParams();

  useEffect(() => {
    if (id) {
      getUniversity(id);
      fetchAgents(id);
    }
  }, [id]);

  const { mutate, isLoading } = useMutation(postData, {
    onSuccess(response) {
      toast.success(data?.id ? "Data updated Successfully" : "Data added Successfully");
      setData({
        ...data,
        id: response?.data?.data?.id,
      });
      refetchUniData();
    },
    onError() {
      toast.error(data?.id ? "Error Updating Data" : "Error Submitting Data");
    },
  });

  async function postData(payload) {
    if (payload?.uniId) {
      const response = await axios.put(`/university/${payload?.uniId}`, payload);
      return response;
    } else {
      const response = await axios.post(`/university`, payload);
      return response;
    }
  }

  const getUniversityData = async (payload) => {
    const response = await axios.get(`/university/${payload}`);
    return response;
  };

  const { mutate: getUniversity } = useMutation(getUniversityData, {
    onSuccess(response) {
      setData(response?.data?.data);
    },
  });

  const { mutate: deleteCourse } = useMutation(deleteCourseData, {
    onSuccess() {
      toast.success("Course deleted successfully");
      // refetchUniData();
      getUniversity(data?.id);
    },
    onError() {
      toast.error("Error deleting course");
    },
  });
  async function deleteCourseData(courseId) {
    const payload = {
      ids: [courseId],
    };
    const response = await axios.delete(`/university/course`, {
      data: payload,
    });
    return response;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!data?.id) {
      if (uniData?.find((item) => item?.abn && item?.abn?.trim() === data?.abn?.trim())) {
        toast.error("University/College with this CRICOS/RTO Code already exists");
        return;
      }
      if (uniData?.find((item) => item?.name && item?.name?.trim() === data?.name?.trim())) {
        toast.error("University/College with this name already exists");
        return;
      }
      if (uniData?.find((item) => item?.email && item?.email?.trim() === data?.email?.trim())) {
        toast.error("University/College with this contact email already exists");
        return;
      }
    }
    mutate({
      uniId: data?.id,
      name: data?.name,
      abn: data?.abn,
      contactPerson: data?.contactPerson,
      email: data?.email,
      country: data?.country,
      state: "",
      zipCode: data?.zipCode,
      image: data?.image,
      pricelistUrl: data?.pricelistUrl,
      applicationFormUrl: data?.applicationFormUrl,
      gsDocumentsUrl: data?.gsDocumentsUrl,
      creditFormUrl: data?.creditFormUrl,
      ...(data?.id ? {} : { orgOnly }),
    });
  };

  const removeCourse = (index, course) => {
    if (course?.courseName) {
      if (window.confirm("Are you sure you want to remove this course from the university?")) {
        // setData((prev) => ({
        //   ...prev,
        //   courses: prev.courses.filter((_, i) => i !== index),
        // }));
        deleteCourse(course?.courseId);
      } else {
        return;
      }
    } else {
      setData((prev) => ({
        ...prev,
        courses: prev.courses.filter((_, i) => i !== index),
      }));
    }
  };

  const closeFeesModal = () => {
    setFeesModalOpen(false);
    setSelectedCourse(null);
    setSelectedCourseIndex(null);
  };

  const saveFeesBreakdown = () => {
    setData((prev) => ({
      ...prev,
      courses: prev?.courses?.map((course, index) =>
        index === selectedCourseIndex ? selectedCourse : course
      ),
    }));
    closeFeesModal();
  };

  const closeScholarshipModal = () => {
    setScholarshipModalOpen(false);
    setSelectedScholarshipCourse(null);
    setSelectedCourseIndex(null);
  };

  const saveScholarship = () => {
    setData((prev) => ({
      ...prev,
      courses: prev.courses.map((course, index) =>
        index === selectedCourseIndex ? selectedScholarshipCourse : course
      ),
    }));
    closeScholarshipModal();
  };

  const goToAddCourse = () => {
    if (!data?.id) return;
    navigate(`/admin/university/${data.id}/course/add`, {
      state: { universityName: data?.name },
    });
  };

  const goToEditCourse = (course) => {
    if (!data?.id || !course?.courseId) return;
    navigate(`/admin/university/${data.id}/course/edit/${course.courseId}`, {
      state: { universityName: data?.name, course },
    });
  };

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-10 py-3 border-0">
            <div className="flex flex-wrap items-center">
              <div className="relative w-full max-w-full flex justify-start gap-4 items-center">
                <IoArrowBack className="text-xl cursor-pointer" onClick={() => navigate(-1)} />
                <h3 className={"font-semibold text-xl text-app-text"}>
                  {data?.id ? "Edit" : "Add"} {data?.id ? data?.name : "University/College"}
                </h3>
              </div>
            </div>
          </div>

          <div className="block w-full overflow-x-auto mt-8">
            <div className="flex-auto lg:px-10 py-10 pt-0">
              <form className="space-y-4 border rounded-lg p-4 bg-app-surface shadow-sm">
                {/* University Image Section */}
                <div className="bg-gradient-to-r from-app-surface-alt to-app-surface-muted rounded-lg p-4 border border-blue-100">
                  <h3 className="text-base font-semibold text-app-text mb-3 flex items-center gap-2">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MdBusiness className="w-4 h-4" />
                    </div>
                    University/College Logo
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-1">
                      {data?.image && (
                        <div className="relative w-[120px] h-[120px] mx-auto mb-3 group">
                          <img
                            src={data?.image}
                            alt="University/College Logo"
                            className="w-full h-full object-cover rounded-lg"
                          />
                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  if (
                                    window.confirm("Are you sure you want to delete this image?")
                                  ) {
                                    setData({ ...data, image: null });
                                  }
                                }}
                                className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition-colors duration-200"
                                title="Delete Image"
                              >
                                <AiOutlineDelete className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const fileInput = document.querySelector('input[type="file"]');
                                  if (fileInput) {
                                    fileInput.click();
                                  }
                                }}
                                className="p-1.5 bg-blue-500 hover:bg-app-primary text-white rounded transition-colors duration-200"
                                title="Change Image"
                              >
                                <AiOutlineEdit className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      <UploadFile
                        data={data}
                        setData={setData}
                        imageKey="image"
                        label="Upload Logo"
                        type="single"
                        required={false}
                        newDesign={true}
                        className="w-full"
                      />
                      <UploadFile
                        data={data}
                        setData={setData}
                        imageKey="pricelistUrl"
                        label="Price List"
                        type="single"
                        required={false}
                        newDesign={false}
                        className="w-full"
                      />
                      <UploadFile
                        data={data}
                        setData={setData}
                        imageKey="applicationFormUrl"
                        label="Application Form"
                        type="single"
                        required={false}
                        newDesign={false}
                        className="w-full"
                      />
                      <UploadFile
                        data={data}
                        setData={setData}
                        imageKey="gsDocumentsUrl"
                        label="GS Documents"
                        type="single"
                        required={false}
                        newDesign={false}
                        className="w-full"
                      />
                      <UploadFile
                        data={data}
                        setData={setData}
                        imageKey="creditFormUrl"
                        label="Credit Form"
                        type="single"
                        required={false}
                        newDesign={false}
                        className="w-full"
                      />
                    </div>
                    <div className="lg:col-span-2 space-y-3">
                      {/* Basic Information */}
                      <div className="bg-app-surface rounded-lg p-3 border border-app-border">
                        <h4 className="text-sm font-semibold text-app-text mb-2 flex items-center gap-2 mb-4">
                          <div className="w-5 h-5 bg-green-100 rounded flex items-center justify-center">
                            <MdSchool className="text-green-600 w-3 h-3" />
                          </div>
                          Basic Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <InputField
                            label="University/College Name"
                            placeholder="Enter university/college name"
                            name="name"
                            required
                            type="text"
                            value={data?.name}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                          <InputField
                            label="CRICOS/RTO Code"
                            placeholder="Enter CRICOS or RTO code"
                            name="abn"
                            type="text"
                            value={data?.abn}
                            onChange={handleInputChange}
                            className="w-full"
                          />

                          <InputField
                            label="Country"
                            placeholder="Enter country name"
                            name="country"
                            type="text"
                            value={data?.country}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                        </div>
                        {!data?.id && (
                          <div className="mt-4">
                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-app-text-secondary">
                              Who can see this university/college?
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => setOrgOnly(false)}
                                className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors duration-150 ${
                                  !orgOnly
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-app-border bg-app-surface hover:bg-app-surface-alt"
                                }`}
                              >
                                <MdPublic
                                  className={`mt-0.5 w-4 h-4 flex-shrink-0 ${
                                    !orgOnly ? "text-blue-600" : "text-app-text-muted"
                                  }`}
                                />
                                <span>
                                  <span
                                    className={`block text-sm font-medium ${
                                      !orgOnly ? "text-blue-800" : "text-app-text"
                                    }`}
                                  >
                                    Shared with every organization
                                  </span>
                                  <span className="block text-xs text-app-text-muted mt-0.5">
                                    Default. Visible and usable by all organizations on the
                                    platform.
                                  </span>
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => setOrgOnly(true)}
                                className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors duration-150 ${
                                  orgOnly
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-app-border bg-app-surface hover:bg-app-surface-alt"
                                }`}
                              >
                                <MdLock
                                  className={`mt-0.5 w-4 h-4 flex-shrink-0 ${
                                    orgOnly ? "text-blue-600" : "text-app-text-muted"
                                  }`}
                                />
                                <span>
                                  <span
                                    className={`block text-sm font-medium ${
                                      orgOnly ? "text-blue-800" : "text-app-text"
                                    }`}
                                  >
                                    Only my organization
                                  </span>
                                  <span className="block text-xs text-app-text-muted mt-0.5">
                                    Hidden from every other organization on the platform.
                                  </span>
                                </span>
                              </button>
                            </div>
                            <p className="text-xs text-app-text-muted mt-2">
                              This applies only when creating — it can't be changed later from this
                              form.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Contact Information */}
                      <div className="bg-app-surface rounded-lg p-3 border border-app-border">
                        <h4 className="text-sm font-semibold text-app-text flex items-center gap-2 mb-4">
                          <div className="w-5 h-5 bg-purple-100 rounded flex items-center justify-center">
                            <MdEmail className="text-purple-600 w-3 h-3" />
                          </div>
                          Contact Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <InputField
                            type="email"
                            placeholder="Enter university/college email"
                            name="email"
                            label="Email Address"
                            value={data?.email}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                          <InputField
                            type="text"
                            placeholder="Enter contact person name"
                            name="contactPerson"
                            label="Contact Person"
                            value={data?.contactPerson}
                            onChange={handleInputChange}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-3 border-t border-app-border">
                  <Button
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 text-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    className="px-4 py-2 text-sm"
                    onClick={handleSubmit}
                  >
                    {data?.id ? "Update" : "Create"} University/College
                  </Button>
                </div>
              </form>
              {data?.id ? (
                <div className="mt-8 bg-app-surface rounded-xl shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <MdSchool className="text-indigo-600 text-xl" />
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-app-text">Course Details</h4>
                        <p className="text-app-text-secondary text-sm">
                          Manage university/college courses and programs
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="contained"
                      onClick={goToAddCourse}
                      className="px-4 py-2"
                      startIcon={<MdSchool />}
                    >
                      Add Course
                    </Button>
                  </div>

                  <div className="overflow-x-auto overflow-y-auto max-h-[500px] rounded-lg border border-app-border">
                    <table className="min-w-full bg-app-surface">
                      <thead className="sticky top-0 bg-app-surface-muted z-10">
                        <tr className="border-b border-app-border">
                          <th className="px-6 py-4 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider min-w-[500px] max-w-[500px]">
                            Course Name
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider min-w-[150px]">
                            Duration (Weeks)
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider min-w-[150px]">
                            No. of Semesters
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider min-w-[300px] max-w-[300px]">
                            Total Fee After Scholarship
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider min-w-[100px]">
                            Created
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider min-w-[100px]">
                            Updated
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-medium text-app-text-secondary uppercase tracking-wider min-w-[100px]">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-app-surface divide-y divide-gray-200">
                        {data?.courses?.map((course, index) => (
                          <tr
                            key={index}
                            className="hover:bg-app-surface-hover transition-colors duration-200"
                          >
                            <td className="px-6 py-4 min-w-[400px] max-w-[500px]">
                              <div
                                className="flex items-center cursor-pointer"
                                // onClick={() => toggleEditMode(course, index)}
                              >
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                  <MdSchool className=" text-sm" />
                                </div>
                                <span className="text-sm font-medium text-app-text cursor-pointer">
                                  {course?.courseName || "-"}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap min-w-[150px]">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {course?.durationInWeeks || "-"} weeks
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap min-w-[150px]">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {course?.noOfSem || "-"} semesters
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap min-w-[300px]">
                              <div className="flex flex-wrap gap-1">
                                {data?.courses[index]?.intakes?.map((intake, i) => (
                                  <span
                                    key={i}
                                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800"
                                  >
                                    {intake}
                                  </span>
                                )) || "-"}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap min-w-[100px]">
                              <span className="text-sm font-medium text-app-text flex flex-col mb-1">
                                {formatDisplayDate(course?.createdDate)} {course?.createdBy}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap min-w-[100px]">
                              <span className="text-sm font-medium text-app-text mb-1 flex flex-col ">
                                {formatDisplayDate(course?.updatedDate)} {course?.updatedBy}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap min-w-[100px]">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => goToEditCourse(course)}
                                  className="p-2  hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                  title="Edit Course"
                                >
                                  <AiOutlineEdit className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeCourse(index, course)}
                                  className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                  title="Delete Course"
                                >
                                  <AiOutlineDelete className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(!data?.courses || data?.courses?.length === 0) && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-app-surface-muted rounded-full flex items-center justify-center mx-auto mb-4">
                        <MdSchool className="text-app-text-muted text-2xl" />
                      </div>
                      <h3 className="text-lg font-medium text-app-text mb-2">
                        No courses added yet
                      </h3>
                      <p className="text-app-text-secondary mb-4">
                        Get started by adding the first course to this university.
                      </p>
                      <Button
                        variant="contained"
                        onClick={goToAddCourse}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        startIcon={<MdSchool />}
                      >
                        Add First Course
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-8 bg-app-surface rounded-xl shadow-sm border p-8 text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MdSchool className="text-yellow-600 text-2xl" />
                  </div>
                  <h3 className="text-lg font-medium text-app-text mb-2">University Required</h3>
                  <p className="text-app-text-secondary mb-4">
                    Please create the university first before adding courses.
                  </p>
                  <p className="text-sm text-app-text-muted">
                    Complete the form above and submit to get started with course management.
                  </p>
                </div>
              )}

              {/* Agents Section */}
              {data?.id && (
                <div className="mt-8 bg-app-surface rounded-xl shadow-sm border p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <MdPerson className="text-green-600 text-xl" />
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-app-text">Agents</h4>
                        <p className="text-app-text-secondary text-sm">
                          Agents linked to this university — used in admission and provider flows
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setAgentForm({ name: "", email: "", phone: "" });
                        setAgentDialogOpen(true);
                      }}
                      startIcon={<MdPerson />}
                    >
                      Add Agent
                    </Button>
                  </div>

                  {agents.length === 0 ? (
                    <div className="text-center py-8 text-app-text-muted italic text-sm">
                      No agents yet. Add agents to pre-fill agent name in admission and provider
                      flows.
                    </div>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-app-border">
                      <table className="min-w-full bg-app-surface">
                        <thead className="bg-app-surface-muted">
                          <tr className="border-b border-app-border">
                            <th className="px-4 py-3 text-left text-xs font-medium text-app-text-secondary uppercase">
                              Name
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-app-text-secondary uppercase">
                              Email
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-app-text-secondary uppercase">
                              Phone
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-app-text-secondary uppercase">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {agents.map((agent) => (
                            <tr key={agent.id} className="hover:bg-app-surface-hover">
                              <td className="px-4 py-3 text-sm font-medium text-app-text">
                                {agent.name}
                              </td>
                              <td className="px-4 py-3 text-sm text-app-text-secondary">
                                {agent.email || "—"}
                              </td>
                              <td className="px-4 py-3 text-sm text-app-text-secondary">
                                {agent.phone || "—"}
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => handleDeleteAgent(agent.id)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Delete agent"
                                >
                                  <AiOutlineDelete className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Agents Dialog */}
      <Dialog
        open={agentDialogOpen}
        onClose={() => setAgentDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Agent</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={agentForm.name}
            onChange={(e) => setAgentForm((p) => ({ ...p, name: e.target.value }))}
            fullWidth
            size="small"
            margin="dense"
            required
          />
          <TextField
            label="Email (optional)"
            value={agentForm.email}
            onChange={(e) => setAgentForm((p) => ({ ...p, email: e.target.value }))}
            fullWidth
            size="small"
            margin="dense"
          />
          <TextField
            label="Phone (optional)"
            value={agentForm.phone}
            onChange={(e) => setAgentForm((p) => ({ ...p, phone: e.target.value }))}
            fullWidth
            size="small"
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddAgent}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fees Breakdown Modal */}
      <FeesBreakdownModal
        open={feesModalOpen}
        onClose={closeFeesModal}
        selectedCourse={selectedCourse}
        setSelectedCourse={setSelectedCourse}
        onSave={saveFeesBreakdown}
      />

      {/* Scholarship Modal */}
      <ScholarshipModal
        open={scholarshipModalOpen}
        onClose={closeScholarshipModal}
        selectedScholarshipCourse={selectedScholarshipCourse}
        setSelectedScholarshipCourse={setSelectedScholarshipCourse}
        onSave={saveScholarship}
      />

      {isLoading && <WholeScreenLoader open={isLoading} />}
    </div>
  );
};

export default AddUni;
